import { html, svg, render, nothing, type TemplateResult } from "lit-html";
import { showTip, hideTip, tipRow } from "./tooltip.js";

export interface Series {
  name: string;
  values: (number | null)[];
  color: string; // CSS color or var()
  dashed?: boolean;
  area?: boolean;
  muted?: boolean;
}

/** Round axis ticks: 0 → a "nice" max in ~4 steps. */
export function niceTicks(min: number, max: number, count = 4): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 1];
  if (min === max) { max = min === 0 ? 1 : min * 1.2; }
  const lo = Math.min(0, min);
  const span = max - lo;
  const raw = span / count;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? 10 * mag;
  const start = Math.floor(lo / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= max + step * 0.001; v += step) ticks.push(Math.round(v / step) * step);
  if (ticks[ticks.length - 1]! < max) ticks.push(ticks[ticks.length - 1]! + step);
  return ticks;
}

abstract class SizedChart<D> extends HTMLElement {
  protected _data: D | null = null;
  protected width = 0;
  private ro: ResizeObserver | null = null;

  set data(d: D) {
    this._data = d;
    this.draw();
  }
  get data(): D | null {
    return this._data;
  }
  connectedCallback() {
    this.style.display = "block";
    this.ro = new ResizeObserver((entries) => {
      const w = Math.floor(entries[0]!.contentRect.width);
      if (w !== this.width) { this.width = w; this.draw(); }
    });
    this.ro.observe(this);
  }
  disconnectedCallback() {
    this.ro?.disconnect();
    this.ro = null;
    hideTip();
  }
  protected draw() {
    if (!this._data || this.width <= 0) return;
    render(this.template(this._data, this.width), this);
  }
  protected abstract template(d: D, w: number): TemplateResult;
}

/* ------------------------------------------------------------------ */
/* Line chart with crosshair                                           */
/* ------------------------------------------------------------------ */

export interface LineData {
  labels: string[];
  series: Series[];
  format: (n: number) => string;
  height?: number;
  highlightIndex?: number | null; // e.g. the partial current month
}

let chartSeq = 0;

export class LineChart extends SizedChart<LineData> {
  private hoverIndex: number | null = null;
  private uid = `lc${++chartSeq}`;

  protected template(d: LineData, w: number): TemplateResult {
    const h = d.height ?? 260;
    const m = { t: 12, r: 14, b: 26, l: 56 };
    const iw = Math.max(10, w - m.l - m.r), ih = h - m.t - m.b;
    const all = d.series.flatMap((s) => s.values.filter((v): v is number => v !== null));
    const ticks = niceTicks(Math.min(0, ...all), Math.max(1, ...all));
    const y0 = ticks[0]!, y1 = ticks[ticks.length - 1]!;
    const n = d.labels.length;
    const x = (i: number) => m.l + (n <= 1 ? iw / 2 : (i / (n - 1)) * iw);
    const y = (v: number) => m.t + ih - ((v - y0) / (y1 - y0)) * ih;

    const pathFor = (vals: (number | null)[]) => {
      let p = "", open = false;
      vals.forEach((v, i) => {
        if (v === null) { open = false; return; }
        p += `${open ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
        open = true;
      });
      return p;
    };
    const areaFor = (vals: (number | null)[]) => {
      const idx = vals.map((v, i) => (v === null ? -1 : i)).filter((i) => i >= 0);
      if (idx.length < 2) return "";
      return `${pathFor(vals)}L${x(idx[idx.length - 1]!)},${y(Math.max(0, y0))}L${x(idx[0]!)},${y(Math.max(0, y0))}Z`;
    };
    const labelEvery = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(iw / 56))));
    const hi = this.hoverIndex;

    const onMove = (e: PointerEvent) => {
      const rect = (e.currentTarget as SVGRectElement).getBoundingClientRect();
      const px = e.clientX - rect.left;
      const i = n <= 1 ? 0 : Math.max(0, Math.min(n - 1, Math.round((px / rect.width) * (n - 1))));
      if (i !== this.hoverIndex) { this.hoverIndex = i; this.draw(); }
      showTip(html`<div class="tt-title">${d.labels[i]}</div>${d.series.map((s) => tipRow(s.name, s.values[i] === null || s.values[i] === undefined ? "—" : d.format(s.values[i]!), s.color))}`, e.clientX, e.clientY);
    };
    const onLeave = () => { this.hoverIndex = null; hideTip(); this.draw(); };

    return html`<svg class="chart ${hi !== null ? "hovering" : ""}" width=${w} height=${h} viewBox="0 0 ${w} ${h}" role="img" aria-label=${d.series.map((s) => s.name).join(", ")}>
      <defs>${d.series.filter((s) => s.area).map((s, i) => svg`<linearGradient id="${this.uid}-a${i}" x1="0" x2="0" y1="0" y2="1"><stop offset="0" style="stop-color:${s.color};stop-opacity:.28"/><stop offset="1" style="stop-color:${s.color};stop-opacity:0"/></linearGradient>`)}</defs>
      ${ticks.map((t) => svg`<line class="grid-line" x1=${m.l} x2=${w - m.r} y1=${y(t)} y2=${y(t)}></line><text x=${m.l - 10} y=${y(t) + 4} text-anchor="end">${d.format(t)}</text>`)}
      ${d.labels.map((l, i) => (i % labelEvery === 0 ? svg`<text x=${x(i)} y=${h - 6} text-anchor="middle">${l}</text>` : nothing))}
      ${d.series.filter((s) => s.area).map((s, i) => svg`<path d=${areaFor(s.values)} fill="url(#${this.uid}-a${i})"></path>`)}
      ${d.series.map((s) => svg`<path d=${pathFor(s.values)} fill="none" style="stroke:${s.color}" stroke-width=${s.muted ? 1.6 : 2.2} stroke-dasharray=${s.dashed ? "5 5" : "none"} stroke-linecap="round" stroke-linejoin="round" opacity=${s.muted ? 0.8 : 1}></path>`)}
      ${d.highlightIndex !== null && d.highlightIndex !== undefined
        ? d.series.filter((s) => !s.muted && !s.dashed).map((s) => {
            const v = s.values[d.highlightIndex!];
            return v === null || v === undefined ? nothing : svg`<circle cx=${x(d.highlightIndex!)} cy=${y(v)} r="4" style="fill:var(--surface-chart);stroke:${s.color}" stroke-width="2"></circle>`;
          })
        : nothing}
      ${hi !== null ? svg`<line class="hover-line" x1=${x(hi)} x2=${x(hi)} y1=${m.t} y2=${m.t + ih}></line>
        ${d.series.map((s) => { const v = s.values[hi]; return v === null || v === undefined ? nothing : svg`<circle cx=${x(hi)} cy=${y(v)} r="4.5" style="fill:${s.color};stroke:var(--surface-chart)" stroke-width="2"></circle>`; })}` : nothing}
      <rect x=${m.l} y=${m.t} width=${iw} height=${ih} fill="transparent" @pointermove=${onMove} @pointerleave=${onLeave}></rect>
    </svg>`;
  }
}

/* ------------------------------------------------------------------ */
/* Column chart (time on x), optional prior-year ghost columns         */
/* ------------------------------------------------------------------ */

export interface ColumnData {
  labels: string[];
  values: (number | null)[];
  prior?: (number | null)[];
  name: string;
  priorName?: string;
  format: (n: number) => string;
  height?: number;
}

export class ColumnChart extends SizedChart<ColumnData> {
  private hoverIndex: number | null = null;

  protected template(d: ColumnData, w: number): TemplateResult {
    const h = d.height ?? 240;
    const m = { t: 10, r: 8, b: 26, l: 56 };
    const iw = Math.max(10, w - m.l - m.r), ih = h - m.t - m.b;
    const nums = [...d.values, ...(d.prior ?? [])].filter((v): v is number => v !== null);
    const ticks = niceTicks(Math.min(0, ...nums), Math.max(1, ...nums));
    const y0 = ticks[0]!, y1 = ticks[ticks.length - 1]!;
    const y = (v: number) => m.t + ih - ((v - y0) / (y1 - y0)) * ih;
    const n = d.labels.length;
    const band = iw / Math.max(1, n);
    const gap = 2;
    const groupW = Math.min(band * 0.72, 56);
    const bw = d.prior ? (groupW - gap) / 2 : groupW;
    const labelEvery = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(iw / 56))));
    const base = y(Math.max(0, y0));
    const bar = (cx: number, v: number | null, fill: string, op = 1) => {
      if (v === null) return nothing;
      const top = Math.min(y(v), base), hh = Math.abs(base - y(v));
      const r = Math.min(4, bw / 2, hh);
      // rounded data-end only (top for positive, bottom for negative)
      const p = v >= 0
        ? `M${cx},${base}V${top + r}Q${cx},${top} ${cx + r},${top}H${cx + bw - r}Q${cx + bw},${top} ${cx + bw},${top + r}V${base}Z`
        : `M${cx},${base}V${base + hh - r}Q${cx},${base + hh} ${cx + r},${base + hh}H${cx + bw - r}Q${cx + bw},${base + hh} ${cx + bw},${base + hh - r}V${base}Z`;
      return svg`<path d=${p} style="fill:${fill}" opacity=${op}></path>`;
    };
    const onMove = (e: PointerEvent) => {
      const rect = (e.currentTarget as SVGRectElement).getBoundingClientRect();
      const i = Math.max(0, Math.min(n - 1, Math.floor(((e.clientX - rect.left) / rect.width) * n)));
      if (i !== this.hoverIndex) { this.hoverIndex = i; this.draw(); }
      const v = d.values[i], p = d.prior?.[i];
      showTip(html`<div class="tt-title">${d.labels[i]}</div>${tipRow(d.name, v === null || v === undefined ? "—" : d.format(v), "var(--series-1)")}
        ${d.prior ? tipRow(d.priorName ?? "Prior", p === null || p === undefined ? "—" : d.format(p), "var(--series-muted)") : nothing}`, e.clientX, e.clientY);
    };
    const onLeave = () => { this.hoverIndex = null; hideTip(); this.draw(); };
    return html`<svg class="chart" width=${w} height=${h} viewBox="0 0 ${w} ${h}" role="img" aria-label=${d.name}>
      ${ticks.map((t) => svg`<line class="grid-line" x1=${m.l} x2=${w - m.r} y1=${y(t)} y2=${y(t)}></line><text x=${m.l - 10} y=${y(t) + 4} text-anchor="end">${d.format(t)}</text>`)}
      ${d.labels.map((l, i) => {
        const cx = m.l + band * i + (band - groupW) / 2;
        const hov = this.hoverIndex === i;
        return svg`${hov ? svg`<rect x=${m.l + band * i} y=${m.t} width=${band} height=${ih} style="fill:var(--glass)" rx="6"></rect>` : nothing}
          ${d.prior ? bar(cx, d.prior[i] ?? null, "var(--series-muted)", 0.55) : nothing}
          ${bar(d.prior ? cx + bw + gap : cx, d.values[i] ?? null, "var(--series-1)")}
          ${i % labelEvery === 0 ? svg`<text x=${m.l + band * i + band / 2} y=${h - 6} text-anchor="middle">${l}</text>` : nothing}`;
      })}
      <line class="axis-line" x1=${m.l} x2=${w - m.r} y1=${base} y2=${base}></line>
      <rect x=${m.l} y=${m.t} width=${iw} height=${ih} fill="transparent" @pointermove=${onMove} @pointerleave=${onLeave}></rect>
    </svg>`;
  }
}

customElements.define("ff-line", LineChart);
customElements.define("ff-columns", ColumnChart);

/* ------------------------------------------------------------------ */
/* Horizontal bars (categories), HTML so long labels behave            */
/* ------------------------------------------------------------------ */

export interface HBarItem { label: string; value: number; ghost?: number | null; onClick?: () => void; title?: string }

export function hbars(items: HBarItem[], format: (n: number) => string, opts: { ghostName?: string; empty?: string } = {}): TemplateResult {
  if (!items.length) return html`<div class="empty-state">${opts.empty ?? "No data for this selection."}</div>`;
  const max = Math.max(1, ...items.map((i) => Math.max(i.value, i.ghost ?? 0)));
  return html`<div class="hbars">${items.map((it) => {
    const tip = (e: PointerEvent) => showTip(html`<div class="tt-title">${it.label}</div>${tipRow("Value", format(it.value), "var(--series-1)")}${it.ghost !== undefined && it.ghost !== null ? tipRow(opts.ghostName ?? "Prior", format(it.ghost)) : nothing}`, e.clientX, e.clientY);
    return html`<div class="hbar" style=${it.onClick ? "cursor:pointer" : ""} @click=${it.onClick ?? nothing} @pointermove=${tip} @pointerleave=${hideTip}>
      <span class="lbl" title=${it.title ?? it.label}>${it.label}</span>
      <span class="track">
        <span class="fill" style="width:${Math.max(0, (it.value / max) * 100).toFixed(2)}%"></span>
        ${it.ghost !== undefined && it.ghost !== null && it.ghost > 0 ? html`<span class="ghost" style="left:calc(${((it.ghost / max) * 100).toFixed(2)}% - 1px)"></span>` : nothing}
      </span>
      <span class="val">${format(it.value)}</span>
    </div>`;
  })}</div>`;
}
